// Image analysis helpers shared across the designer.
// KEEP IN SYNC with the original logic from CrossStitchApp.jsx.

const clamp01 = (value) => Math.max(0, Math.min(1, value));
const clampInt = (value, min, max) =>
  Math.min(Math.max(Math.round(value ?? 0), min), max);

const FRACTIONAL_SLOTS = 4;
const FRACTIONAL_QUADRANT_OFFSETS = [
  [0, 0], // NW (quadrant 0)
  [0, 1], // NE (quadrant 1)
  [1, 0], // SW (quadrant 2)
  [1, 1], // SE (quadrant 3)
];
const FRACTIONAL_INTENSITY_THRESHOLDS = [0.4, 0.6, 0.75, 0.87, 0.94, 0.985];

const CARTOON_DEFAULTS = {
  colorLevels: 6,
  edgeThreshold: 0.23,
  edgeColor: "#111111",
  edgeOpacity: 0.9,
  smoothIterations: 1,
};

const quantizeChannel = (value, levels) => {
  if (!levels || levels <= 1) return value;
  const step = 255 / (levels - 1);
  return Math.round(value / step) * step;
};

const hexToRgb = (hex) => {
  if (!hex) return [0, 0, 0];
  let normalized = hex.replace("#", "");
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((char) => char + char)
      .join("");
  }
  const int = parseInt(normalized, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
};

const srgbToLinear = (value) =>
  value <= 0.04045
    ? value / 12.92
    : Math.pow((value + 0.055) / 1.055, 2.4);

const rgbToLab = (r, g, b) => {
  const R = srgbToLinear(r / 255);
  const G = srgbToLinear(g / 255);
  const B = srgbToLinear(b / 255);

  const X = R * 0.4124 + G * 0.3576 + B * 0.1805;
  const Y = R * 0.2126 + G * 0.7152 + B * 0.0722;
  const Z = R * 0.0193 + G * 0.1192 + B * 0.9505;

  const refX = 0.95047;
  const refY = 1;
  const refZ = 1.08883;

  const pivot = (value) =>
    value > 0.008856
      ? Math.pow(value, 1 / 3)
      : 7.787 * value + 16 / 116;

  const fx = pivot(X / refX);
  const fy = pivot(Y / refY);
  const fz = pivot(Z / refZ);

  return {
    L: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
};

const labDistance = (labA, labB) => {
  const dl = labA.L - labB.L;
  const da = labA.a - labB.a;
  const db = labA.b - labB.b;
  return Math.sqrt(dl * dl + da * da + db * db);
};

function cartoonifyImageData(
  imgData,
  width,
  height,
  userOptions = {}
) {
  const options = { ...CARTOON_DEFAULTS, ...userOptions };
  const {
    colorLevels,
    edgeThreshold,
    edgeColor,
    edgeOpacity,
    smoothIterations,
  } = options;

  const edgeColorRgb = hexToRgb(edgeColor);
  const alpha = clamp01(edgeOpacity ?? 1);
  const { data } = imgData;
  const pixelCount = width * height;

  const grayscale = new Float32Array(pixelCount);
  for (let i = 0; i < pixelCount; i++) {
    const offset = i * 4;
    const r = data[offset];
    const g = data[offset + 1];
    const b = data[offset + 2];

    grayscale[i] = 0.299 * r + 0.587 * g + 0.114 * b;

    data[offset] = quantizeChannel(r, colorLevels);
    data[offset + 1] = quantizeChannel(g, colorLevels);
    data[offset + 2] = quantizeChannel(b, colorLevels);
  }

  let gradientSource = grayscale;
  if (smoothIterations > 0 && width > 2 && height > 2) {
    let current = grayscale;
    let next = new Float32Array(pixelCount);
    for (let iter = 0; iter < smoothIterations; iter++) {
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          let sum = 0;
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const idx = (y + ky) * width + (x + kx);
              sum += current[idx];
            }
          }
          next[y * width + x] = sum / 9;
        }
      }
      const temp = current;
      current = next;
      next = temp;
    }
    gradientSource = current;
  }

  const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
  const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];

  const mixChannel = (base, overlay) =>
    Math.round(base * (1 - alpha) + overlay * alpha);

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      let gx = 0;
      let gy = 0;
      let kernelIndex = 0;
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const sample =
            gradientSource[(y + ky) * width + (x + kx)];
          gx += sobelX[kernelIndex] * sample;
          gy += sobelY[kernelIndex] * sample;
          kernelIndex++;
        }
      }
      const magnitude = Math.sqrt(gx * gx + gy * gy);
      const normalized = Math.min(1, magnitude / 1020);
      if (normalized > edgeThreshold) {
        const offset = (y * width + x) * 4;
        data[offset] = mixChannel(data[offset], edgeColorRgb[0]);
        data[offset + 1] = mixChannel(
          data[offset + 1],
          edgeColorRgb[1]
        );
        data[offset + 2] = mixChannel(
          data[offset + 2],
          edgeColorRgb[2]
        );
      }
    }
  }
}

/**
 * Analyze an <img> element and return the dominant colors.
 */
export function extractDominantColors(
  img,
  count,
  sensitivity = 10,
  options = {}
) {
  const tempCanvas = document.createElement("canvas");
  const tempCtx = tempCanvas.getContext("2d");
  tempCanvas.width = img.width;
  tempCanvas.height = img.height;

  try {
    tempCtx.drawImage(img, 0, 0);
  } catch (error) {
    console.error(
      "Error drawing image to temp canvas for color extraction:",
      error
    );
    tempCanvas.remove();
    return [];
  }

  let imgData;
  try {
    imgData = tempCtx.getImageData(0, 0, img.width, img.height);
  } catch (error) {
    console.error(
      "Security error or draw failure during color extraction:",
      error
    );
    tempCanvas.remove();
    return [];
  }

  if (options?.cartoonify) {
    cartoonifyImageData(
      imgData,
      img.width,
      img.height,
      options.cartoonOptions
    );
  }

  const { data } = imgData;
  const colorMap = new Map();

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    if (a < 128) continue;

    const clusterR = Math.floor(r / sensitivity) * sensitivity;
    const clusterG = Math.floor(g / sensitivity) * sensitivity;
    const clusterB = Math.floor(b / sensitivity) * sensitivity;

    const key = `${clusterR},${clusterG},${clusterB}`;
    const current = colorMap.get(key) || { count: 0, r: 0, g: 0, b: 0 };

    current.count += 1;
    current.r += r;
    current.g += g;
    current.b += b;
    colorMap.set(key, current);
  }

  const sortedColors = Array.from(colorMap.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, count)
    .map((color) => {
      const avgR = Math.floor(color.r / color.count);
      const avgG = Math.floor(color.g / color.count);
      const avgB = Math.floor(color.b / color.count);

      const toHex = (n) => n.toString(16).padStart(2, "0");
      const hex = `#${toHex(avgR)}${toHex(avgG)}${toHex(avgB)}`;

      return {
        hex,
        name: "Auto-Color",
        code: hex.toUpperCase().substring(1, 7),
      };
    });

  tempCanvas.remove();
  return sortedColors;
}

/**
 * Core conversion helper that returns a full-stitch grid at the requested resolution.
 */
function convertImageToGridBasic(
  img,
  palette,
  cols,
  rows,
  options = {}
) {
  if (palette.length === 0) {
    console.warn("Palette is empty, cannot convert image.");
    return new Int32Array(cols * rows).fill(-1);
  }

  const tempCanvas = document.createElement("canvas");
  const tempCtx = tempCanvas.getContext("2d");

  const finalWidth = cols;
  const finalHeight = rows;
  tempCanvas.width = finalWidth;
  tempCanvas.height = finalHeight;

  try {
    tempCtx.drawImage(img, 0, 0, finalWidth, finalHeight);
  } catch (error) {
    console.error("Error drawing image to temp canvas for conversion:", error);
    tempCanvas.remove();
    return new Int32Array(cols * rows).fill(-1);
  }

  let imgData;
  try {
    imgData = tempCtx.getImageData(0, 0, finalWidth, finalHeight);
  } catch (error) {
    console.error("Could not read resampled image data:", error);
    tempCanvas.remove();
    return new Int32Array(cols * rows).fill(-1);
  }

  if (options?.cartoonify) {
    cartoonifyImageData(
      imgData,
      finalWidth,
      finalHeight,
      options.cartoonOptions
    );
  }

  const processedData = new Float32Array(imgData.data.length);
  for (let i = 0; i < imgData.data.length; i++) {
    processedData[i] = imgData.data[i] / 255;
  }
  const newCellData = new Int32Array(cols * rows).fill(-1);

  const normalizedPalette = palette.map((color) => {
    const [r255, g255, b255] = hexToRgb(color.hex);
    return {
      r: r255 / 255,
      g: g255 / 255,
      b: b255 / 255,
      lab: rgbToLab(r255, g255, b255),
    };
  });

  const findNearestColor = (pixelLab) => {
    let closestIndex = -1;
    let minDistanceSq = Infinity;

    normalizedPalette.forEach((target, index) => {
      const distance = labDistance(pixelLab, target.lab);
      if (distance < minDistanceSq) {
        minDistanceSq = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const pixelIndex = (row * cols + col) * 4;

      const oldR = processedData[pixelIndex];
      const oldG = processedData[pixelIndex + 1];
      const oldB = processedData[pixelIndex + 2];
      const oldA = processedData[pixelIndex + 3];

      if (oldA < 0.5) continue;

      const pixelLab = rgbToLab(
        Math.round(oldR * 255),
        Math.round(oldG * 255),
        Math.round(oldB * 255)
      );

      const closestIndex = findNearestColor(pixelLab);
      if (closestIndex !== -1) {
        const newColor = normalizedPalette[closestIndex];
        const errorR = oldR - newColor.r;
        const errorG = oldG - newColor.g;
        const errorB = oldB - newColor.b;

        newCellData[row * cols + col] = closestIndex;

        const scatterError = (targetRow, targetCol, factor) => {
          if (
            targetRow < 0 ||
            targetRow >= rows ||
            targetCol < 0 ||
            targetCol >= cols
          ) {
            return;
          }

          const targetIndex = (targetRow * cols + targetCol) * 4;
          processedData[targetIndex] = clamp01(
            processedData[targetIndex] + errorR * factor
          );
          processedData[targetIndex + 1] = clamp01(
            processedData[targetIndex + 1] + errorG * factor
          );
          processedData[targetIndex + 2] = clamp01(
            processedData[targetIndex + 2] + errorB * factor
          );
        };

        if (col + 1 < cols) {
          scatterError(row, col + 1, 7 / 16);
        }
        if (row + 1 < rows && col > 0) {
          scatterError(row + 1, col - 1, 3 / 16);
        }
        if (row + 1 < rows) {
          scatterError(row + 1, col, 5 / 16);
        }
        if (row + 1 < rows && col + 1 < cols) {
          scatterError(row + 1, col + 1, 1 / 16);
        }
      }
    }
  }

  tempCanvas.remove();
  return newCellData;
}

/**
 * Convert an <img> into palette indices. When fractionalDetail is enabled, also
 * return a quadratic subdivision that maps each quadrant of a stitch cell.
 */
export function convertImageToGrid(
  img,
  palette,
  cols,
  rows,
  options = {}
) {
  const baseCells = convertImageToGridBasic(img, palette, cols, rows, options);

  if (!options?.fractionalDetail || cols <= 0 || rows <= 0) {
    return baseCells;
  }

  const fractionalCells = new Int32Array(cols * rows * FRACTIONAL_SLOTS).fill(-1);
  const intensityIndex = clampInt(
    options.fractionalIntensity ?? 3,
    0,
    FRACTIONAL_INTENSITY_THRESHOLDS.length - 1
  );
  const intensityThreshold = FRACTIONAL_INTENSITY_THRESHOLDS[intensityIndex];
  const highCols = cols * 2;
  const highRows = rows * 2;
  const highResCells = convertImageToGridBasic(
    img,
    palette,
    highCols,
    highRows,
    options
  );

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const cellIndex = row * cols + col;
      const fractionalBase = cellIndex * FRACTIONAL_SLOTS;
      const quadrantValues = [-1, -1, -1, -1];
      let filledCount = 0;
      const colorCounts = new Map();

      for (let q = 0; q < FRACTIONAL_SLOTS; q++) {
        const [offsetRow, offsetCol] = FRACTIONAL_QUADRANT_OFFSETS[q];
        const subRow = row * 2 + offsetRow;
        const subCol = col * 2 + offsetCol;
        const subIndex = subRow * highCols + subCol;
        const colorIdx = highResCells[subIndex];
        quadrantValues[q] = colorIdx;
        if (colorIdx >= 0) {
          filledCount += 1;
          colorCounts.set(colorIdx, (colorCounts.get(colorIdx) || 0) + 1);
        }
      }

      const uniqueColors = colorCounts.size;
      const isPartialCoverage = filledCount > 0 && filledCount < FRACTIONAL_SLOTS;
      const majorityCount = colorCounts.size
        ? Math.max(...colorCounts.values())
        : 0;
      const coverageRatio = filledCount > 0 ? majorityCount / filledCount : 1;
      const intensitySatisfied = isPartialCoverage
        ? true
        : coverageRatio <= intensityThreshold;

      // Only promote to fractional stitches if the cell isn't a solid fill, and
      // the requested intensity allows it.
      const requiresFractional =
        filledCount > 0 &&
        (isPartialCoverage || (uniqueColors > 1 && intensitySatisfied));

      if (!requiresFractional) {
        continue;
      }

      let majorityColor = -1;
      if (colorCounts.size) {
        majorityColor = [...colorCounts.entries()].reduce((best, entry) =>
          entry[1] > best[1] ? entry : best
        )[0];
      }

      if (majorityColor >= 0) {
        for (let q = 0; q < FRACTIONAL_SLOTS; q++) {
          if (quadrantValues[q] < 0) {
            quadrantValues[q] = majorityColor;
          }
        }
      }

      for (let q = 0; q < FRACTIONAL_SLOTS; q++) {
        fractionalCells[fractionalBase + q] = quadrantValues[q];
      }
      if (majorityColor >= 0) {
        baseCells[cellIndex] = majorityColor;
      } else {
        baseCells[cellIndex] = -1;
      }
    }
  }

  return {
    cells: baseCells,
    fractionalCells,
  };
}
